import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft size={20} className="mr-2" />
                Back to LuckySend
              </Link>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            <div className="w-32"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose prose-gray max-w-none">
            <div className="text-sm text-gray-500 mb-6">
              Last updated: May 1, 2026
            </div>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                LuckySend collects the following types of information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><strong>Email credentials:</strong> Your email address and authentication details for sending emails</li>
                <li><strong>Email content:</strong> The HTML content and subject lines you create</li>
                <li><strong>Attachment information:</strong> File names and metadata of attachments you upload</li>
                <li><strong>Usage data:</strong> How you interact with our service (for analytics and improvement)</li>
                <li><strong>Technical data:</strong> IP addresses, browser information, and device details</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the collected information to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Process and send your emails as requested</li>
                <li>Provide customer support and technical assistance</li>
                <li>Improve our service and develop new features</li>
                <li>Ensure security and prevent abuse of our service</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Data Storage and Security</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  <strong>Temporary Processing:</strong> Email content and attachments are processed temporarily
                  during the sending process and are not permanently stored on our servers.
                </p>
                <p>
                  <strong>Security Measures:</strong> We implement appropriate technical and organizational
                  measures to protect your personal information against unauthorized access, alteration,
                  disclosure, or destruction.
                </p>
                <p>
                  <strong>Data Retention:</strong> We retain your email credentials and usage data only as
                  long as necessary for the purposes outlined in this policy.
                </p>
                <p>
                  <strong>OAuth Security:</strong> LuckySend uses Google OAuth 2.0 for authentication. We never
                  see, touch, or store your Google account password. Access tokens are stored locally in your
                  browser and are immediately discarded when you log out.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Information Sharing</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>When required by law or to protect our rights</li>
                <li>With service providers who assist in operating our service (under strict confidentiality agreements)</li>
                <li>In connection with a business transfer or acquisition</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use cookies and similar technologies to enhance your experience. In addition,
                <strong> third-party vendors, including Google</strong>, use cookies to serve ads on
                LuckySend based on your prior visits to this and other websites. Google's advertising
                cookies allow it and its partners to serve interest-based ads to you.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                Specifically, Google AdSense may place and read cookies on your browser, and may use
                web beacons and similar technologies to collect information as a result of ad serving
                on this website. This data collection is governed by{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                  Google's Privacy Policy
                </a>.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You can opt out of personalized advertising via{' '}
                <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                  Google Ads Settings
                </a>
                , or control cookies through your browser preferences. Some features may not function
                properly without certain cookies enabled.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Advertising — Google AdSense</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                LuckySend uses Google AdSense to display advertisements on our website. Google AdSense uses
                cookies to serve ads based on your prior visits to this and other websites. Google's use of
                advertising cookies enables it and its partners to serve ads to you based on your visit to
                LuckySend and/or other sites on the Internet.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may opt out of personalized advertising by visiting{' '}
                <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                  Google Ads Settings
                </a>. For more information about how Google uses data when you use their partners' sites or apps, see{' '}
                <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                  How Google uses data
                </a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our service integrates with third-party email providers (such as Gmail) and uses
                Google AdSense for advertising. These third parties have their own privacy policies:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Google Privacy Policy</a></li>
                <li><a href="https://support.google.com/adsense/answer/48182" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Google AdSense Program Policies</a></li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to or restrict certain types of processing</li>
                <li>Data portability (receive your data in a structured format)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                LuckySend is not intended for children under 13 years of age. We do not knowingly
                collect personal information from children under 13. If you become aware that a child
                has provided us with personal information, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes
                by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at{' '}
                <a href="mailto:support@luckysend.app" className="text-blue-600 hover:text-blue-800">
                  support@luckysend.app
                </a>{' '}or through our website at{' '}
                <a href="https://forouverse.solutions" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                  forouverse.solutions
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
